import * as React from "react";
import { format, parse } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { Input } from "./ui/input";

interface Props {
  value?: string;
  onChange: (date?: string) => void;
  withTime?: boolean;
}

export const DatePicker: React.FC<Props> = ({ value, onChange, withTime = false }) => {
  const { t } = useTranslation();
  const dateFormat = withTime ? "dd-MM-yyyy HH:mm" : "dd-MM-yyyy";
  const date = value ? parse(value, dateFormat, new Date()) : undefined;

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!date) {
      // If no date is selected, use today's date with selected time
      const today = new Date();
      const [hours, minutes] = e.target.value.split(":");
      today.setHours(parseInt(hours), parseInt(minutes));
      onChange(format(today, dateFormat));
    } else {
      // Update existing date with new time
      const [hours, minutes] = e.target.value.split(":");
      date.setHours(parseInt(hours), parseInt(minutes));
      onChange(format(date, dateFormat));
    }
  };

  const handleDateChange = (newDate: Date | undefined) => {
    if (!newDate) {
      onChange(undefined);
      return;
    }

    if (withTime && date) {
      // Preserve existing time when updating date
      newDate.setHours(date.getHours(), date.getMinutes());
    }
    onChange(format(newDate, dateFormat));
  };

  const getTime = (date: Date) => {
    try {
      return format(date, "HH:mm");
    } catch (error) {
      return "00:00";
    }
  };

  return (
    <div className="flex flex-row gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn("w-full justify-start text-left font-normal", !value && "text-muted-foreground")}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value || <span>{t("common.pickDate")}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar mode="single" selected={date} onSelect={handleDateChange} initialFocus fromDate={new Date()} />
        </PopoverContent>
      </Popover>
      {withTime && (
        <Input className="w-[140px]" type="time" value={date ? getTime(date) : undefined} onChange={handleTimeChange} />
      )}
    </div>
  );
};
